/**
 * Header Component
 * Top Navigation mit Logo und Hamburger Menu für Mobile
 */

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useLanguage, useT, STRINGS } from '@/context/LanguageContext'
import './Header.css'

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const t = useT()
  const { lang, toggle } = useLanguage()

  const closeMenu = () => setMenuOpen(false)

  // HomePage gets a transparent header so the 3D hero carousel reads
  // edge-to-edge. The artwork detail page does the same when the hero
  // image / 3D viewer fills the viewport — once the user scrolls past
  // ~40px the header switches to solid so the title doesn't bleed
  // through.
  const isHome = location.pathname === '/'
  const isWerkDetail = location.pathname.startsWith('/werk/')
  const wantsScrollTransparent = isHome || isWerkDetail

  useEffect(() => {
    if (!wantsScrollTransparent) {
      setScrolled(false)
      return
    }
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [wantsScrollTransparent, location.pathname])

  const transparent = wantsScrollTransparent && !scrolled

  return (
    <header className={`header ${transparent ? 'header-transparent' : ''}`}>
      <div className="header-container">
        {/* Logo / Artist Name + small round portrait (Hermine #43 — users
            are sensitive to faces and connect with the work via the artist) */}
        <Link to="/" className="header-logo" onClick={closeMenu}>
          <img
            className="header-portrait"
            src="https://api-storage.arkserver.arkturian.com/storage/media/36183?width=120&height=120"
            alt="Anna Sacher Santana"
            loading="eager"
          />
          <span className="header-logo-text">
            <span className="logo-text">Anna Sacher Santana</span>
            <span className="logo-subtitle">
              {lang === 'de' ? 'Skulpturen' : 'Sculptures'}
            </span>
          </span>
        </Link>

        {/* Hamburger Button (Mobile only) */}
        <button
          className={`hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>

        {/* Navigation */}
        <nav className={`header-nav ${menuOpen ? 'open' : ''}`}>
          <Link to="/" className="nav-link" onClick={closeMenu}>{t(STRINGS.nav.home)}</Link>
          <Link to="/galerie" className="nav-link" onClick={closeMenu}>{t(STRINGS.nav.werke)}</Link>
          <Link to="/kuenstlerin" className="nav-link" onClick={closeMenu}>{t(STRINGS.nav.kuenstlerin)}</Link>
          <Link to="/ausstellungen" className="nav-link" onClick={closeMenu}>{t(STRINGS.nav.ausstellungen)}</Link>
          <Link to="/kunst-fuer-dich" className="nav-link" onClick={closeMenu}>{t(STRINGS.nav.kunstFuerDich)}</Link>
          <Link to="/kontakt" className="nav-link nav-link-contact" onClick={closeMenu}>{t(STRINGS.common.contact)}</Link>
          <button
            type="button"
            className="nav-link nav-lang-toggle"
            onClick={() => {
              toggle()
              closeMenu()
            }}
            title={t(STRINGS.langSwitch.title)}
            aria-label={t(STRINGS.langSwitch.title)}
          >
            {lang === 'de' ? 'EN' : 'DE'}
          </button>
        </nav>
      </div>

      {/* Mobile Overlay */}
      {menuOpen && <div className="nav-overlay" onClick={closeMenu} />}
    </header>
  )
}
